/**
 * The buyer funnel: from a person's dated events to where they stand.
 *
 * Every status is entered by an event the system records (an order paid, an
 * email received, a file read, a carrier scan, a session), so a status always
 * has a "since". Waiting longer than a status's normal time makes a person
 * stuck there. Statuses that need us to act are blocked.
 *
 * After purchase two tracks run side by side, Kit and Prescription, and meet
 * at "activated". Definition and the reasons: docs/funnel-definition.md.
 */

export type FunnelTrack = 'start' | 'kit' | 'prescription' | 'app'

export const FUNNEL_STEPS = [
  { id: 'purchased', label: 'Purchased', track: 'start' },
  { id: 'shipped', label: 'Kit shipped', track: 'kit' },
  { id: 'delivered', label: 'Kit delivered', track: 'kit' },
  { id: 'asked', label: 'Prescription asked for', track: 'prescription' },
  { id: 'sent', label: 'Prescription sent', track: 'prescription' },
  { id: 'read', label: 'Prescription read', track: 'prescription' },
  { id: 'eligible', label: 'Eligible', track: 'prescription' },
  { id: 'activated', label: 'Account activated', track: 'app' },
  { id: 'started', label: 'Started training', track: 'app' },
  { id: 'onboarded', label: 'Onboarded', track: 'app' },
] as const satisfies ReadonlyArray<{ id: string; label: string; track: FunnelTrack }>

export type FunnelStepId = (typeof FUNNEL_STEPS)[number]['id']

/** Everything the funnel needs about one person, as dates (ISO strings) or plain facts. */
export interface FunnelFacts {
  paidAt: string | null
  refundedAt: string | null
  refundFailed: boolean
  /* Kit */
  /** Whether anything physical ships: an order with a shipping method, or a
      shipment on record. App-only and wire-transfer buyers have no kit. Absent
      means yes, as for every kit buyer. */
  shipsKit?: boolean
  fulfilledAt: string | null
  /** The carrier's latest status for the newest shipment, and when it was reported. */
  carrierStatus: 'label_created' | 'in_transit' | 'exception' | 'out_for_delivery' | 'delivered' | 'returned' | null
  carrierStatusAt: string | null
  /* Prescription */
  askedAt: string | null
  remindedAt: string | null
  reminders: number
  unreachable: boolean
  /** A mail from the buyer with no attachment. */
  wroteAt: string | null
  /** A file arrived: a mail attachment, or a hand upload. */
  fileAt: string | null
  /** The newest reader verdict, and when the file it read arrived. */
  verdict: 'prescription' | 'not_prescription' | 'unreadable' | 'failed' | null
  verdictAt: string | null
  hasDateOfBirth: boolean
  grade: 'suitable' | 'not_suitable' | 'refer' | 'needs_answers' | 'incomplete' | null
  gradedAt: string | null
  review: 'not_reviewed' | 'approved' | 'need_research' | 'out_of_criteria' | 'declined' | null
  reviewedAt: string | null
  /* App */
  /** Cortex account status: 'Demo' until an operator makes it real. Null with no account. */
  accountStatus: string | null
  firstSessionAt: string | null
  fifthSessionAt: string | null
  sessions: number
}

export interface StepState {
  /** Short machine name of the status inside the step. */
  status: string
  label: string
  done: boolean
  /** Needs us to act. */
  blocked: boolean
  /** Waiting longer than this status's normal time. */
  stuck: boolean
  since: string | null
}

export type FunnelExit = 'refunded' | 'not_eligible'

export interface PersonFunnel {
  steps: Record<FunnelStepId, StepState>
  exit: FunnelExit | null
  refundFailed: boolean
  /** The first step not done, in funnel order; null once onboarded or exited. */
  current: FunnelStepId | null
}

/** Normal time per status, in days. First guesses; tune as data arrives. */
export const FUNNEL_NORMAL_DAYS: Record<string, number> = {
  'shipped:not_shipped': 3,
  'shipped:label_created': 2,
  'delivered:in_transit': 7,
  'delivered:out_for_delivery': 1,
  'asked:not_asked': 1,
  'sent:nothing': 7,
  'sent:wrote_no_file': 3,
  'read:reading': 1,
  'eligible:graded': 2,
  'eligible:need_research': 3,
  'activated:waiting': 2,
  'started:not_started': 3,
  'onboarded:training': 14,
}

const DAY = 86_400_000
const latest = (...xs: Array<string | null>) => xs.filter((x): x is string => !!x).sort().pop() ?? null

export function computeFunnel(f: FunnelFacts, now: Date = new Date()): PersonFunnel {
  const state = (step: FunnelStepId, status: string, label: string, since: string | null, opts: { done?: boolean; blocked?: boolean } = {}): StepState => {
    const normal = FUNNEL_NORMAL_DAYS[`${step}:${status}`]
    const waited = since ? (now.getTime() - new Date(since).getTime()) / DAY : 0
    return {
      status,
      label,
      done: opts.done ?? false,
      blocked: opts.blocked ?? false,
      stuck: !opts.done && !opts.blocked && normal != null && since != null && waited > normal,
      since,
    }
  }

  const purchased = f.paidAt
    ? state('purchased', 'paid', 'Paid', f.paidAt, { done: true })
    : state('purchased', 'not_paid', 'Not paid', null)

  /* Kit */
  const delivered = f.carrierStatus === 'delivered'
  const moving = f.carrierStatus && f.carrierStatus !== 'label_created'
  const noKit = f.shipsKit === false
  const shipped = noKit
    ? state('shipped', 'no_kit', 'No kit to ship', null, { done: true })
    : moving
    ? state('shipped', 'picked_up', 'Picked up by the carrier', f.carrierStatusAt, { done: true })
    : f.fulfilledAt || f.carrierStatus === 'label_created'
      ? state('shipped', 'label_created', 'Label created', latest(f.fulfilledAt, f.carrierStatus === 'label_created' ? f.carrierStatusAt : null))
      : state('shipped', 'not_shipped', 'Not shipped', f.paidAt)
  const deliveredState = noKit
    ? state('delivered', 'no_kit', 'No kit to ship', null, { done: true })
    : delivered
    ? state('delivered', 'delivered', 'Delivered', f.carrierStatusAt, { done: true })
    : f.carrierStatus === 'exception'
      ? state('delivered', 'problem', 'Carrier problem', f.carrierStatusAt, { blocked: true })
      : f.carrierStatus === 'returned'
        ? state('delivered', 'returned', 'Returned', f.carrierStatusAt, { blocked: true })
        : f.carrierStatus === 'out_for_delivery'
          ? state('delivered', 'out_for_delivery', 'Out for delivery', f.carrierStatusAt)
          : f.carrierStatus === 'in_transit'
            ? state('delivered', 'in_transit', 'In transit', f.carrierStatusAt)
            : state('delivered', 'not_yet', 'Not on its way yet', null)

  /* Prescription */
  const asked = f.unreachable
    ? state('asked', 'unreachable', 'Email bounced or unsubscribed', latest(f.askedAt, f.remindedAt), { blocked: true, done: !!f.askedAt })
    : f.remindedAt
      ? state('asked', 'reminded', f.reminders > 1 ? `Reminded ${f.reminders} times` : 'Reminded', f.remindedAt, { done: true })
      : f.askedAt
        ? state('asked', 'asked', 'Asked', f.askedAt, { done: true })
        : state('asked', 'not_asked', 'Not asked yet', f.paidAt)
  /* Any file counts as sent, even one that turns out not to be a prescription;
     reading it is the next step's business. */
  const sent = f.fileAt
    ? state('sent', 'file_sent', 'File sent', f.fileAt, { done: true })
    : f.wroteAt
      ? state('sent', 'wrote_no_file', 'Wrote, no file', f.wroteAt)
      : state('sent', 'nothing', 'Nothing yet', latest(f.askedAt, f.paidAt))
  const read = !f.fileAt
    ? state('read', 'nothing', 'Nothing to read', null)
    : f.verdict === 'prescription' && !f.hasDateOfBirth
      ? state('read', 'no_date_of_birth', 'Read, date of birth missing', f.verdictAt, { blocked: true })
      : f.verdict === 'prescription'
        ? state('read', 'read', 'Read', f.verdictAt, { done: true })
        : f.verdict === 'not_prescription'
          ? state('read', 'not_prescription', 'Not a prescription', f.verdictAt, { blocked: true })
          : f.verdict === 'unreadable' || f.verdict === 'failed'
            ? state('read', 'unreadable', 'Could not be read', f.verdictAt, { blocked: true })
            : state('read', 'reading', 'Reading', f.fileAt)
  const notEligible = f.review === 'out_of_criteria' || f.review === 'declined'
  const eligible =
    f.review === 'approved'
      ? state('eligible', 'approved', 'Approved', f.reviewedAt, { done: true })
      : notEligible
        ? state('eligible', 'not_eligible', f.review === 'declined' ? 'Declined' : 'Out of criteria', f.reviewedAt)
        : f.review === 'need_research'
          ? state('eligible', 'need_research', 'Needs research', f.reviewedAt)
          : f.grade
            ? state('eligible', 'graded', `Graded: ${GRADE_LABEL[f.grade]}`, f.gradedAt)
            : state('eligible', 'not_graded', 'Not graded yet', null)

  /* App: activation waits for both tracks. */
  const ready = deliveredState.done && eligible.done
  const realAccount = !!f.accountStatus && f.accountStatus !== 'Demo'
  const activated = realAccount
    ? state('activated', 'activated', 'Activated', null, { done: true })
    : ready
      ? state('activated', 'waiting', 'Ready, still a demo account', latest(deliveredState.since, eligible.since))
      : state('activated', 'not_ready', f.accountStatus === 'Demo' ? 'Demo account' : 'No account', null)
  /* A session proves an account, whatever its status says. */
  const started = f.firstSessionAt
    ? state('started', 'started', 'Started', f.firstSessionAt, { done: true })
    : realAccount
      ? state('started', 'not_started', 'Not started', null)
      : state('started', 'not_ready', 'Not activated yet', null)
  const onboarded = f.fifthSessionAt || f.sessions >= 5
    ? state('onboarded', 'onboarded', 'Onboarded', f.fifthSessionAt, { done: true })
    : f.firstSessionAt
      ? state('onboarded', 'training', `${f.sessions} of 5 sessions`, f.firstSessionAt)
      : state('onboarded', 'not_started', 'Not started', null)

  const steps: Record<FunnelStepId, StepState> = {
    purchased, shipped, delivered: deliveredState, asked, sent, read, eligible, activated, started, onboarded,
  }
  if (started.done) steps.activated = { ...activated, done: true, status: 'activated', label: 'Activated' }

  const exit: FunnelExit | null = f.refundedAt ? 'refunded' : notEligible ? 'not_eligible' : null
  const current = exit ? null : (FUNNEL_STEPS.find((s) => !steps[s.id].done)?.id ?? null)
  return { steps, exit, refundFailed: f.refundFailed, current }
}

const GRADE_LABEL: Record<NonNullable<FunnelFacts['grade']>, string> = {
  suitable: 'suitable',
  not_suitable: 'not suitable',
  refer: 'refer',
  needs_answers: 'needs answers',
  incomplete: 'incomplete',
}

/* ------------------------- the Kit × Prescription grid ------------------------- */

export const GRID_KIT = [
  { id: 'not_shipped', label: 'Not shipped' },
  { id: 'on_its_way', label: 'Shipped, on its way' },
  { id: 'problem', label: 'Carrier problem or returned' },
  { id: 'delivered', label: 'Delivered' },
] as const
export const GRID_PRESCRIPTION = [
  { id: 'nothing', label: 'Not sent' },
  { id: 'sent', label: 'Sent, not read' },
  { id: 'read', label: 'Read' },
  { id: 'eligible', label: 'Eligible' },
] as const

export type GridKit = (typeof GRID_KIT)[number]['id']
export type GridPrescription = (typeof GRID_PRESCRIPTION)[number]['id']

/** Where a person sits in the grid where the two tracks meet. */
export function gridCell(p: PersonFunnel): { kit: GridKit; prescription: GridPrescription } {
  const d = p.steps.delivered
  const kit: GridKit = d.done
    ? 'delivered'
    : d.status === 'problem' || d.status === 'returned'
      ? 'problem'
      : p.steps.shipped.status === 'not_shipped'
        ? 'not_shipped'
        : 'on_its_way'
  const prescription: GridPrescription = p.steps.eligible.done
    ? 'eligible'
    : p.steps.read.done
      ? 'read'
      : p.steps.sent.done
        ? 'sent'
        : 'nothing'
  return { kit, prescription }
}

/* ------------------------------ picking people ------------------------------ */

/**
 * What somebody pressed on a funnel: a state inside a step, "here now" at a
 * step, a square of the Kit × Prescription grid, or an exit. The screen and the
 * Users list it opens both go through `funnelPickMatches`, so the list always
 * holds exactly the people the number counted.
 */
export type FunnelPick =
  | { kind: 'status'; step: FunnelStepId; status: string }
  | { kind: 'here'; step: FunnelStepId }
  | { kind: 'cell'; kit: GridKit; prescription: GridPrescription }
  | { kind: 'exit'; exit: FunnelExit }

const firstUndone = (p: PersonFunnel, track: FunnelTrack) =>
  FUNNEL_STEPS.find((s) => s.track === track && !p.steps[s.id].done)?.id

/**
 * Whether this step is where the person is now. Kit and Prescription run side
 * by side, so it is asked per track: the first step not done in that track.
 * The app steps only start once both tracks are done.
 */
export function isHereAt(p: PersonFunnel, step: FunnelStepId): boolean {
  const track = FUNNEL_STEPS.find((s) => s.id === step)!.track
  if (track === 'app' && (firstUndone(p, 'kit') || firstUndone(p, 'prescription'))) return false
  return firstUndone(p, track) === step
}

export function funnelPickMatches(p: PersonFunnel, pick: FunnelPick): boolean {
  if (pick.kind === 'exit') return p.exit === pick.exit
  if (p.exit) return false
  if (pick.kind === 'status') return p.steps[pick.step].status === pick.status
  if (pick.kind === 'cell') {
    const c = gridCell(p)
    return c.kit === pick.kit && c.prescription === pick.prescription
  }
  return isHereAt(p, pick.step)
}

/**
 * The step whose state a list shows beside each person, and whose "since" is
 * their wait: the step pressed, or for a grid square or an exit, the first
 * thing still open (prescription first, then kit).
 */
export function funnelPickStep(p: PersonFunnel, pick: FunnelPick): FunnelStepId {
  if (pick.kind === 'status' || pick.kind === 'here') return pick.step
  return firstUndone(p, 'prescription') ?? firstUndone(p, 'kit') ?? 'onboarded'
}

/* In an address: "status:shipped:label_created", "here:read",
   "cell:delivered:read", "exit:refunded". */
export function formatFunnelPick(pick: FunnelPick): string {
  switch (pick.kind) {
    case 'status': return `status:${pick.step}:${pick.status}`
    case 'here': return `here:${pick.step}`
    case 'cell': return `cell:${pick.kit}:${pick.prescription}`
    case 'exit': return `exit:${pick.exit}`
  }
}

export function parseFunnelPick(s: string): FunnelPick | null {
  const [kind, a, b] = s.split(':')
  const isStep = (x: string | undefined): x is FunnelStepId => FUNNEL_STEPS.some((st) => st.id === x)
  if (kind === 'status' && isStep(a) && b) return { kind, step: a, status: b }
  if (kind === 'here' && isStep(a)) return { kind, step: a }
  if (kind === 'cell' && GRID_KIT.some((k) => k.id === a) && GRID_PRESCRIPTION.some((r) => r.id === b)) {
    return { kind, kit: a as GridKit, prescription: b as GridPrescription }
  }
  if (kind === 'exit' && (a === 'refunded' || a === 'not_eligible')) return { kind, exit: a }
  return null
}

/** A heading for the list a pick opens: "Kit delivered: In transit". */
export function describeFunnelPick(pick: FunnelPick, statusLabel?: string): string {
  const stepLabel = (id: FunnelStepId) => FUNNEL_STEPS.find((s) => s.id === id)!.label
  switch (pick.kind) {
    case 'status': return `${stepLabel(pick.step)}: ${statusLabel ?? pick.status}`
    case 'here': return `At "${stepLabel(pick.step)}" now`
    case 'cell': return `Kit ${GRID_KIT.find((k) => k.id === pick.kit)!.label.toLowerCase()} · prescription ${GRID_PRESCRIPTION.find((r) => r.id === pick.prescription)!.label.toLowerCase()}`
    case 'exit': return pick.exit === 'refunded' ? 'Left the funnel: refunded' : 'Left the funnel: not eligible'
  }
}
